import { useEffect, useState } from "react";
import axios from "axios";
import { Printer, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Full-section (non-popover) version of the AWB print queue. Rendered
// inside the Settings page — same behavior as the previous top-bar
// dropdown (fetch queue, print all, mark printed), just always expanded.
export default function PrintQueueSection() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/print-queue`);
      setItems(r.data?.items || []);
    } catch (_e) { /* polling best-effort */ }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  const printAll = async () => {
    if (!items.length || busy) return;
    setBusy(true); setMsg("Se pregătește PDF-ul...");
    const snapshot = items.slice();
    setItems([]);
    try {
      const orders = snapshot.map((it) => ({ id: it.order_id, integration_key: it.integration_key }));
      const idsToMark = snapshot.map((it) => it.composite_id || it.order_id).filter(Boolean);
      const [r] = await Promise.all([
        axios.post(`${API}/orders/bulk/awb-download`, { orders }, { responseType: "blob" }),
        axios.post(`${API}/print-queue/mark-printed`, { order_ids: idsToMark }).catch(() => null),
      ]);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (!w) {
        setMsg("Popup blocat — permite popup-uri pentru bragus-app.");
        setBusy(false);
        return;
      }
      setMsg(`${snapshot.length} AWB-uri trimise la print. Apasă Cmd+P (Mac) sau Ctrl+P (Win) în tab-ul deschis.`);
      setTimeout(() => { setBusy(false); setMsg(""); }, 4000);
    } catch (e) {
      setMsg("Eroare: " + (e?.response?.data?.detail || e.message));
      setItems(snapshot);
      setBusy(false);
    }
  };

  const removeOne = async (it) => {
    if (!window.confirm(`Scoți AWB-ul #${it.order_id} din coadă (nu se va mai printa)?`)) return;
    try {
      await axios.post(`${API}/print-queue/mark-printed`, { order_ids: [it.composite_id || it.order_id] });
      setItems((prev) => prev.filter((x) => (x.composite_id || x.order_id) !== (it.composite_id || it.order_id)));
    } catch (e) {
      setMsg("Eroare: " + (e?.response?.data?.detail || e.message));
    }
  };

  const count = items.length;

  return (
    <section
      data-testid="print-queue-section"
      className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6"
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-cyan-500/15 text-cyan-300">
            <Printer className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-zinc-100">Coada de printare AWB</h2>
            <p className="text-[11px] text-zinc-500">
              {count === 0 ? "Coada e goală" : `${count} ${count === 1 ? "AWB pending" : "AWB-uri pending"}`}
              {" · "}se reîmprospătează automat la 15s
            </p>
          </div>
        </div>
        <button
          onClick={printAll}
          disabled={!count || busy}
          data-testid="print-queue-section-print-all"
          className="inline-flex items-center gap-1.5 rounded-md bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-cyan-500 disabled:opacity-40"
        >
          <Printer className="h-3.5 w-3.5" /> {busy ? "..." : "Printează toate"}
        </button>
      </div>

      {msg && (
        <div className="mb-3 rounded-md border border-cyan-800/40 bg-cyan-950/40 px-3 py-2 text-[11px] text-cyan-200">
          {msg}
        </div>
      )}

      <div className="max-h-80 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950/60">
        {count === 0 && (
          <div className="p-6 text-center text-[11px] text-zinc-500">
            Coada e goală. Configurează o automatizare cu acțiunea &quot;Adaugă AWB în coada de printat&quot;
            sau adaugă AWB-uri manual din pagina Comenzi.
          </div>
        )}
        {items.map((it) => (
          <div
            key={it.composite_id || it.order_id}
            className="flex items-center justify-between border-b border-zinc-900 px-3 py-2 text-xs last:border-b-0"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-zinc-200">{it.customer_name || "—"}</div>
              <div className="truncate text-[11px] text-zinc-500">
                #{it.order_id} · {it.awb_number}
              </div>
            </div>
            <button
              onClick={() => removeOne(it)}
              data-testid={`print-queue-section-remove-${it.order_id}`}
              className="ml-2 rounded p-1 text-zinc-500 transition hover:bg-red-900/30 hover:text-red-400"
              title="Scoate din coadă"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      {count > 0 && (
        <p className="mt-3 text-[10px] text-zinc-500">
          💡 După click pe &quot;Printează toate&quot;: se deschide un tab cu PDF-ul → Cmd+P (Mac) /
          Ctrl+P (Win) → imprimanta ta default.
        </p>
      )}
    </section>
  );
}
